import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Drawer,
  Checkbox,
  Button,
  Tabs,
  Row,
  Col,
  Collapse,
  Slider,
  Input,
  DatePicker,
  Badge,
} from "../../common/utils/antd-imports/AntdImports";
import "./FilterDrawer.css";
import DMAList from "../dma-list/DmaList";
import { ReactComponent as CalenderIcon } from "../../../assests/cuida_calendar-outline.svg";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as SearchIcon } from "../../../assests/search-icon.svg";

import dayjs from "dayjs";
import { getFilterProducts } from "../../../store/thunks/common-thunks/commonThunk";
import { clearSelectedDMAs } from "../../../store/thunks/common-thunks/Actions";
import {
  setDateRange,
  setFiltersApplied,
  setInComeSliderValue,
  setSearchText,
  setSelectedSizes,
  clearUserInteracted,
  clearMapCamera,
  clearSelectedStateId,
  clearSelectedState,
} from "../../../store/slices/common-slice/product-filters/ProductFilter";

const { Panel } = Collapse;

const FilterDrawer = forwardRef(
  (
    {
      visible,
      onClose,
      totalProductCount,
      setProductsMsg,
      handleFiltersApplied,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const { channelData, sizeList, maxDate, minDate, maxPrice, minPrice } =
      useSelector((state) => state.products);

    const selectedDMAs = useSelector((state) => state.dma.selectedDMAs);
    const dmaClearRef = useRef(null);
    const loadingRef = useRef(false);
    const [searchProductTypeByTitle, setSearchProductTypeByTitle] = useState(
      {},
    );
    const [selectedFilters, setSelectedFilters] = useState({});
    const [selectedRestrictions, setSelectedRestrictions] = useState([]);
    const [expandedPanels, setExpandedPanels] = useState({});
    const [isFetching, setIsFetching] = useState(false);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [pageNo, setPageNo] = useState(1);
    const {
      showNearby,
      selectedDateRange,
      selectedSizes,
      inComeSliderValue,
      searchText,
    } = useSelector((state) => state.filters);

    // Helper: flatten selectedFilters to array of objects
    const flattened = Object.values(selectedFilters)
      .flat()
      ?.map((item) => {
        if (typeof item === "string") {
          try {
            return JSON.parse(item);
          } catch {
            return item;
          }
        }
        return item;
      });

    // Helper: check if any filter is active
    const hasActiveFilters = useCallback(
      () =>
        selectedDMAs.length > 0 ||
        selectedSizes.length > 0 ||
        flattened.length > 0 ||
        !!searchText ||
        !!selectedDateRange?.from ||
        !!selectedDateRange?.to,
      [selectedDMAs, selectedSizes, flattened, searchText, selectedDateRange],
    );

    // Build payload: send price values only for pagination, otherwise send empty strings
    const buildPayload = useCallback(
      async (current, isPagination = false, overrides = {}) => {
        if (isFetching) return;
        setIsFetching(true);
        if (current === 1) setIsApplyingFilters(true);

        const dmaIds =
          overrides.dmaIds ??
          (selectedDMAs?.map((item) => item?.id)?.filter(Boolean) || []);

        const product_type =
          overrides.product_type ??
          (Object.values(selectedFilters)
            .flat()
            ?.map((item) =>
              typeof item === "string" ? JSON.parse(item).id : item.id,
            ) ||
            []);
        const finalSizes = overrides.size ?? selectedSizes;
        const finalSearch = overrides.searchText ?? searchText;
        const finalDateRange = overrides.dateRange ?? selectedDateRange;
        const isSearch = !finalSearch;
        const sendEmptyPrices = isSearch && !isPagination;
        setPageNo(current);
        const shouldSendPrices = isPagination || !hasActiveFilters();
        const payload = {
          page_no: current,
          page_size: 8,
          product_type,
          product_fromPrice: sendEmptyPrices
            ? ""
            : shouldSendPrices && inComeSliderValue?.[0] !== null
              ? String(inComeSliderValue[0])
              : "",
          product_toPrice: sendEmptyPrices
            ? ""
            : shouldSendPrices && inComeSliderValue?.[1] !== null
              ? String(inComeSliderValue[1])
              : "",

          booked_from:
            finalDateRange?.from?.format?.("YYYY-MM-DD") ||
            finalDateRange?.from ||
            "",
          booked_to:
            finalDateRange?.to?.format?.("YYYY-MM-DD") ||
            finalDateRange?.to ||
            "",
          size: finalSizes,
          search_param: finalSearch,
          product_dma: dmaIds,
        };
        await dispatch(getFilterProducts(payload));

        setIsFetching(false);
        setIsApplyingFilters(false);
      },
      [
        isFetching,
        selectedDMAs,
        selectedFilters,
        inComeSliderValue,
        selectedDateRange,
        selectedSizes,
        searchText,
        dispatch,
        hasActiveFilters,
      ],
    );

    // Update price slider when min/max change
    useEffect(() => {
      if (minPrice != null && maxPrice != null) {
        setInComeSliderValue([minPrice, maxPrice]);
      }
    }, [minPrice, maxPrice]);
    // Only run buildPayload on mount, not on every render (prevents page auto-increment)
    useEffect(() => {
      buildPayload(1, true); // Initial load with page 1 and forceSendPrice = true
      // eslint-disable-next-line
    }, []);

    // Pagination: always send price values, prevent double increments
    const loadMoreProducts = useCallback(() => {
      if (showNearby) return;
      const pageSize = 8;
      if (isFetching || isApplyingFilters || loadingRef.current) return;
      loadingRef.current = true;
      const maxPages = Math.ceil(totalProductCount / pageSize);

      setPageNo((prevPageNo) => {
        const nextPage = prevPageNo + 1;
        if (nextPage > maxPages) {
          setProductsMsg("Everything’s been loaded. Happy browsing!");
          loadingRef.current = false;
          return prevPageNo;
        }
        setProductsMsg("Pulling up products now...");
        buildPayload(nextPage, true).finally(() => {
          loadingRef.current = false;
        });
        return nextPage;
      });
    }, [
      isFetching,
      isApplyingFilters,
      totalProductCount,
      setProductsMsg,
      buildPayload,
    ]);

    useImperativeHandle(ref, () => ({
      loadMoreProducts,
    }));

    // Handlers
    const handleCheckboxChange = (categoryTitle, checkedValues) => {
      setSelectedFilters((prev) => ({
        ...prev,
        [categoryTitle]: checkedValues,
      }));
    };

    const handleDateChange = useCallback(
      (dates) => {
        dispatch(setInComeSliderValue(["", ""]));
        dispatch(
          setDateRange({
            from: dates?.[0] ? dayjs(dates[0]).toISOString() : null,
            to: dates?.[1] ? dayjs(dates[1]).toISOString() : null,
          }),
        );
      },
      [dispatch],
    );
    const handleTextChange = useCallback(
      (value) =>
        value ? value.replace(value[0], value[0].toUpperCase()) : value,
      [],
    );
    const handleInComeSliderChange = useCallback(
      (value) => {
        dispatch(setInComeSliderValue(value));
      },
      [dispatch],
    );
    const handleIncomeInputChange = useCallback(
      (index, e) => {
        const newValue = [...inComeSliderValue];
        newValue[index] = Number(e.target.value) || "";
        dispatch(setInComeSliderValue(newValue));
      },
      [dispatch, inComeSliderValue],
    );

    const handleSelectedSizesChange = useCallback(
      (sizes) => {
        dispatch(setSelectedSizes(sizes));
      },
      [dispatch],
    );
    const handleViewMore = (title) => {
      setExpandedPanels((prev) => ({
        ...prev,
        [title]: !prev[title],
      }));
    };

    const clearDmaFilters = () => {
      if (dmaClearRef.current) {
        dmaClearRef.current();
        dispatch(clearSelectedDMAs());
      }
    };

    const clearAll = async () => {
      const clearedSizes = [];
      const clearedDateRange = { from: null, to: null };
      const clearedSearch = "";
      const clearedDmas = [];
      dispatch(setSelectedSizes(clearedSizes));
      dispatch(setDateRange(clearedDateRange));
      dispatch(setInComeSliderValue([minPrice, maxPrice]));
      dispatch(setSearchText(clearedSearch));
      clearDmaFilters();
      setSelectedFilters({});
      setSelectedRestrictions([]);
      dispatch(setFiltersApplied(false));
      dispatch(clearUserInteracted(null));
      dispatch(clearMapCamera());
      dispatch(clearSelectedStateId());
      dispatch(clearSelectedState());
      localStorage.removeItem("STATE_SELECTED");
      sessionStorage.removeItem("PAGINATION_ACTIVE");
      sessionStorage.removeItem("mapZoomVal");
      onClose();
      await buildPayload(1, false, {
        size: clearedSizes,
        dateRange: clearedDateRange,
        searchText: clearedSearch,
        dmaIds: clearedDmas,
        product_type: [],
      });
    };

    // Apply handler for all filters
    const handleApply = () => {
      if (isFetching) return; // prevent double click
      if (!hasActiveFilters()) {
        onClose();
        return;
      }
      buildPayload(1, false);
      onClose();
      handleFiltersApplied();
      dispatch(setFiltersApplied(true));
    };

    // Tab items
    const tabItems = [
      {
        label: (
          <span>
            Dates
            {selectedDateRange?.from && selectedDateRange?.to && (
              <Badge
                count={1}
                style={{ backgroundColor: "#d2042d" }}
                className="filters-count-badge"
              />
            )}
          </span>
        ),
        key: "3",
        children: (
          <div className="filter-options">
            <Row className="filter-date-picker">
              <Col span={12}>
                <DatePicker
                  bordered={false}
                  placeholder="From"
                  suffixIcon={<CalenderIcon />}
                  value={
                    selectedDateRange?.from
                      ? dayjs(selectedDateRange?.from, "YYYY-MM-DD")
                      : null
                  }
                  onChange={(date) =>
                    handleDateChange([date, selectedDateRange?.to])
                  }
                  popupClassName="end-date-calendar"
                />
              </Col>
              <Col span={12}>
                <DatePicker
                  bordered={false}
                  placeholder="To"
                  suffixIcon={<CalenderIcon />}
                  value={
                    selectedDateRange?.to
                      ? dayjs(selectedDateRange?.to, "YYYY-MM-DD")
                      : null
                  }
                  onChange={(date) =>
                    handleDateChange([selectedDateRange?.from, date])
                  }
                  popupClassName="end-date-calendar"
                />
              </Col>
            </Row>
          </div>
        ),
      },
      {
        label: (
          <span>
            Location{" "}
            <Badge
              count={selectedDMAs?.length}
              style={{
                backgroundColor: "#d2042d",
                fontSize: "12px",
              }}
              offset={[0, 0]}
              size="default"
            />
          </span>
        ),
        key: "1",
        children: (
          <div className="filter-options">
            <DMAList exposeClearFunction={(fn) => (dmaClearRef.current = fn)} />
          </div>
        ),
      },

      {
        label: (
          <span>
            Product Type{" "}
            <Badge
              count={flattened?.length}
              style={{
                backgroundColor: "#d2042d",
                fontSize: "12px",
              }}
              offset={[0, 0]}
              size="default"
            />
          </span>
        ),
        key: "2",
        children: (
          <div className="filter-options">
            <Collapse
              defaultActiveKey={[]}
              bordered={false}
              className="collapse-container-product-type"
              expandIconPosition="end"
            >
              {channelData &&
                channelData?.map(({ id, title, filter, color_code }) => {
                  const isExpanded = expandedPanels[title];
                  const searchValue = searchProductTypeByTitle[title] || "";
                  const filtered = filter?.filter(({ label }) =>
                    label.toLowerCase().includes(searchValue.toLowerCase()),
                  );
                  const visibleFilters = isExpanded
                    ? filtered
                    : filtered.slice(0, 5);
                  return (
                    <Panel
                      header={
                        <>
                          <span
                            className="dot"
                            style={{
                              backgroundColor: color_code,
                            }}
                          />
                          {title}
                        </>
                      }
                      key={id}
                      className="collapse-pannel-container"
                    >
                      <Input
                        placeholder="Search ProductType"
                        variant="filled"
                        className="search-f-loc"
                        prefix={<SearchIcon />}
                        style={{
                          height: 40,
                          marginBottom: "8px",
                        }}
                        allowClear
                        value={searchValue}
                        onChange={(e) =>
                          setSearchProductTypeByTitle((prev) => ({
                            ...prev,
                            [title]: e.target.value,
                          }))
                        }
                      />
                      <Checkbox.Group
                        options={visibleFilters?.map((f) => ({
                          label: (
                            <>
                              <span
                                className="dot"
                                style={{
                                  backgroundColor: color_code,
                                }}
                              />
                              <span>
                                {f?.label}{" "}
                                <span className="filter-products-count">
                                  ({f?.product_count})
                                </span>
                              </span>
                            </>
                          ),
                          value: JSON.stringify({ id: f?.id, label: f?.label }),
                        }))}
                        value={(selectedFilters[title] || []).map((obj) =>
                          typeof obj === "string" ? obj : JSON.stringify(obj),
                        )}
                        onChange={(checkedValues) => {
                          // Always store as array of objects
                          const parsedValues = checkedValues.map((v) =>
                            typeof v === "string" ? JSON.parse(v) : v,
                          );
                          handleCheckboxChange(title, parsedValues);
                        }}
                        className="collapse-pannel-checkbox"
                      />

                      {filter.length > 5 && (
                        <a
                          className="view-more-loc"
                          onClick={() => handleViewMore(title)}
                        >
                          {isExpanded ? "View Less" : "View More"}
                        </a>
                      )}
                    </Panel>
                  );
                })}
            </Collapse>
          </div>
        ),
      },

      {
        label: "Price",
        key: "4",
        children: (
          <div className="filter-options">
            <Row className="budget-input" gutter={[16, 16]}>
              <Col span={12}>
                <Input
                  className="rfp-input-field"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  inputMode="decimal"
                  pattern="[0-9]*"
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="From"
                  value={inComeSliderValue[0]}
                  onChange={(e) => handleIncomeInputChange(0, e)}
                  prefix="$"
                />
              </Col>
              <Col span={12}>
                <Input
                  className="rfp-input-field"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  inputMode="decimal"
                  pattern="[0-9]*"
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="To"
                  value={inComeSliderValue[1]}
                  onChange={(e) => handleIncomeInputChange(1, e)}
                  prefix="$"
                />
              </Col>
            </Row>
            <div className="slider-parent">
              <Slider
                range
                value={inComeSliderValue}
                min={minPrice}
                max={maxPrice}
                trackStyle={{ backgroundColor: "red" }}
                tooltip={{ open: false }}
                onChange={handleInComeSliderChange}
              />
            </div>
          </div>
        ),
      },
      {
        label: "Size",
        key: "5",
        children: (
          <div className="filter-options">
            <Checkbox.Group
              options={sizeList?.map((item) => handleTextChange(item?.label))}
              value={selectedSizes}
              onChange={handleSelectedSizesChange}
              className="collapse-pannel-checkbox"
            />
          </div>
        ),
      },
    ];

    return (
      <div className="parent-filter-drawer">
        <Drawer
          title="Filter"
          placement="bottom"
          onClose={onClose}
          visible={visible}
          height={550}
          className="filter-drawer "
          footer={
            <div className="drawer-footer">
              <Row justify="space-between">
                <Button className="clear-btn" onClick={clearAll}>
                  Clear all
                </Button>
                <Button
                  type="primary"
                  className="apply-btn"
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </Row>
            </div>
          }
        >
          <div className="nav-bar-list">
            <Tabs
              defaultActiveKey="1"
              items={tabItems}
              tabPosition="left"
              tabBarGutter={20}
              className="filter-tab"
            />
          </div>
        </Drawer>
      </div>
    );
  },
);

export default FilterDrawer;
